import { Button, ButtonProps } from "@chakra-ui/react";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import fr from "date-fns/locale/fr";
import { ForwardedRef, forwardRef, useMemo } from "react";
import ReactDatePicker, { DatePickerProps, registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./DatePicker.css";

registerLocale("fr", fr);

let defaultTimeZone: string | undefined;

/**
 * Sets the IANA timezone every `DatePicker` uses by default (when no `timeZone` prop is passed).
 * The consuming app seeds this once from the active pharmacy so pickers show/return pharmacy-local
 * instants instead of browser-local ones.
 */
export const setDefaultDatePickerTimeZone = (timeZone?: string | null): void => {
  defaultTimeZone = timeZone ?? undefined;
};

export enum DatePickerMode {
  DAY = "day",
  MONTH = "month",
  YEAR = "year",
}

const modeProperties: Record<
  DatePickerMode,
  Pick<
    DatePickerProps,
    | "dateFormat"
    | "scrollableYearDropdown"
    | "showMonthDropdown"
    | "showMonthYearPicker"
    | "showYearDropdown"
    | "showYearPicker"
    | "yearDropdownItemNumber"
  >
> = {
  [DatePickerMode.DAY]: {
    dateFormat: "EEEE dd/MM/yyyy",
    scrollableYearDropdown: true,
    showMonthDropdown: true,
    showYearDropdown: true,
    yearDropdownItemNumber: 100,
  },
  [DatePickerMode.MONTH]: {
    dateFormat: "MMMM yyyy",
    scrollableYearDropdown: true,
    showMonthYearPicker: true,
    showYearDropdown: true,
    yearDropdownItemNumber: 100,
  },
  [DatePickerMode.YEAR]: {
    dateFormat: "yyyy",
    scrollableYearDropdown: true,
    showYearDropdown: true,
    showYearPicker: true,
    yearDropdownItemNumber: 100,
  },
};

interface DatePickerLabelProperties extends Omit<ButtonProps, "children" | "onClick"> {
  placeholder?: string;
  value?: string;
}

const DatePickerLabel = forwardRef(
  (
    { placeholder = "Sélectionner", value, ...rest }: DatePickerLabelProperties,
    reference: ForwardedRef<HTMLButtonElement>,
  ) => {
    return (
      <Button color="gray.600" fontWeight="medium" ref={reference} textDecoration="underline" variant="link" {...rest}>
        {value != null && value.length > 0 ? value : placeholder}
      </Button>
    );
  },
);

export interface DatePickerProperties
  extends Pick<
      DatePickerProps,
      | "endDate"
      | "isClearable"
      | "maxDate"
      | "minDate"
      | "readOnly"
      | "selected"
      | "showTimeSelect"
      | "startDate"
      | "shouldCloseOnSelect"
      | "portalId"
    >,
    Omit<DatePickerLabelProperties, "onChange" | "value"> {
  mode?: DatePickerMode;
  onChange?: (value: string | null, date?: Date) => void;
  /**
   * IANA timezone the picked/displayed wall-clock time refers to. When set, the calendar shows and
   * returns instants in this timezone rather than the browser's. `undefined` falls back to the app
   * default (see `setDefaultDatePickerTimeZone`); pass `null` to force browser-local behavior, which
   * also means date-only modes get their `Date` served as-is.
   */
  timeZone?: string | null;
  value?: string;
}

export const DatePicker = ({
  endDate,
  isClearable,
  isDisabled,
  maxDate,
  minDate,
  mode = DatePickerMode.DAY,
  onChange,
  placeholder,
  readOnly,
  selected,
  shouldCloseOnSelect,
  showTimeSelect,
  portalId,
  startDate,
  timeZone,
  ...rest
}: DatePickerProperties): React.ReactElement => {
  const effectiveTimeZone = timeZone === undefined ? defaultTimeZone : (timeZone ?? undefined);

  // Une date sans heure (mode DAY/MONTH/YEAR) est un jour calendaire pur : elle n'a pas de fuseau,
  // elle ne doit donc jamais être réinterprétée via un fuseau (ni pharmacie ni navigateur) — sinon
  // un jour calendaire peut changer de mois. `react-datepicker` lit `selected`/`startDate`/`endDate`
  // via les accesseurs LOCAUX du `Date` (fuseau du navigateur) : on reconstruit donc un `Date` local
  // à partir des composantes UTC plutôt que de renvoyer l'instant UTC brut.
  // Exception `timeZone: null` : l'appelant annonce fournir des `Date` dont les composantes LOCALES
  // portent déjà le jour à afficher (cf. `TimespanPicker`) — les relire en UTC les décalerait.
  const toDisplayedDate = (date?: Date | null): Date | null | undefined => {
    if (!date) {
      return date;
    }
    if (showTimeSelect) {
      return effectiveTimeZone ? utcToZonedTime(date, effectiveTimeZone) : date;
    }
    if (timeZone === null) {
      return date;
    }
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };

  const handleChange = (date: Date | null) => {
    if (date) {
      if (showTimeSelect) {
        const instant = effectiveTimeZone ? zonedTimeToUtc(date, effectiveTimeZone) : date;
        return onChange?.(instant.toISOString(), date);
      }
      const dayInstant = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      return onChange?.(dayInstant.toISOString(), date);
    }

    if (isClearable) {
      onChange?.(null);
    }
  };

  const pickerProperties = useMemo(() => {
    const base = { ...modeProperties[mode] };
    if (showTimeSelect) {
      base.dateFormat = `${base.dateFormat} HH:mm`;
    }
    return base;
  }, [mode, showTimeSelect]);

  return (
    <ReactDatePicker
      clearButtonClassName="react-datepicker__clear-button"
      customInput={<DatePickerLabel isDisabled={isDisabled} pr={isClearable && selected ? 6 : undefined} {...rest} />}
      disabled={isDisabled}
      endDate={toDisplayedDate(endDate)}
      isClearable={isClearable}
      locale="fr"
      maxDate={maxDate}
      minDate={minDate}
      onChange={handleChange}
      placeholderText={placeholder}
      readOnly={readOnly}
      selected={toDisplayedDate(selected)}
      portalId={portalId}
      showPopperArrow={false}
      showTimeSelect={showTimeSelect}
      startDate={toDisplayedDate(startDate)}
      {...pickerProperties}
      shouldCloseOnSelect={shouldCloseOnSelect ?? true}
      timeCaption="horaire"
    />
  );
};
